import { Accordion, AccordionDetails, AccordionSummary, Grid } from '@material-ui/core';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { Button, Dialog, Icons, Typography } from '@nutrien/cxp-components';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Subscription } from 'rxjs';

import { useMst } from '../../mobx-models/Root';
import { Discipline, InspectionTaskListType } from '../../models/models';
import useDeficiencies from '../../rxdb/deficiencyList/useDeficiencies';
import useSelectedInspection from '../../rxdb/inspectionList/useSelectedInspection';
import type { PmCheckList } from '../../rxdb/pmCheckList/pmCheckList.types';
import usePmCheckList from '../../rxdb/pmCheckList/usePmCheckList';
import type { PreviousTaskCommentsDocument } from '../../rxdb/previousTaskComments/previousTaskComments.types';
import { usePreviousTaskComments } from '../../rxdb/previousTaskComments/usePreviousTaskComments';
import useSavePmChecklist from '../../rxdb/savepmChecklist/useSavePmChecklist';
import useSignAndStamp from '../../rxdb/signAndStamp/useSignAndStamp';
import type { TaskList } from '../../rxdb/taskList/taskList.types';
import useAccessOnTask from '../../rxdb/taskList/useAccessOnTask';
import { useTaskList } from '../../rxdb/taskList/useTaskList';
import { sentryCaptureException } from '../../utilities/sentryCaptureException';
import { useNotification } from '../../utilities/useNotification';
import { InspectionStatus, Key, TaskStatus } from '../../utilities/enums';
import {
  getStatusAndMessageOnReviewFromUtility,
  getStatusAndMessageOnSubmitFromUtility,
  isNull,
} from '../../utilities/utilityFunctions';
import SignAndStamp from '../InspectionListSignAndStamp';
import SignAndStampFI from '../SignAndStamp';
import CompleteInspectionAccordion from './CompleteInspectionAccordion';
import ConfirmationOnAssign from './ConfirmationOnAssign';
import { useStyles } from './CompleteInspection.styles';

dayjs.extend(utc);

const CompleteInspection: React.FC = () => {
  // hooks
  const classes = useStyles();
  const history = useHistory();
  const { successNotification, errorNotification } = useNotification();
  const { saveInspection, CompletionDate, SiteDiscipline, configCatFlag, CurrentUserSelected } =
    useMst();
  const { isFISignAvailable, isLISignAvailable } = useSignAndStamp(
    String(CurrentUserSelected.inspectionId),
    CurrentUserSelected.taskId,
  );
  const { selectedInspection } = useSelectedInspection(
    String(CurrentUserSelected.inspectionId),
    CurrentUserSelected.workPackageId,
  );
  const { pmCheckListLookUpData } = usePmCheckList(SiteDiscipline.siteId);
  const isDECUser = !!CurrentUserSelected?.currentUser?.clerkId;

  const { pmChecklistTransactionData } = useSavePmChecklist();
  const { setTaskLevelComment, setTaskListInfo, setTaskComment, setTaskRecommendation } =
    useTaskList();
  const {
    previousTaskCommentsInitialized,
    previousTaskCommentsCollection,
    getPreviousTaskComments,
  } = usePreviousTaskComments();
  const { deficiencies, activeDeficiencies } = useDeficiencies(
    CurrentUserSelected.taskId,
    CurrentUserSelected.inspectionId,
    Number(CurrentUserSelected.assetId),
    selectedInspection?.assetInspectionStatusId,
  );
  const { isEditable, taskData, isLiFiSame } = useAccessOnTask();

  // states
  const [showComment, setShowComment] = useState<boolean>(false);
  const [isCompleting, setIsCompleting] = useState<boolean>(false);
  const [isSubmitToSC, setIsSubmitToSC] = useState<boolean>(false);
  const [openConfirmationForAssignOrSubmit, setOpenConfirmationForAssignOrSubmit] = useState(false);
  const [isAttentionFlag, setIsAttentionFlag] = useState<boolean>(false);
  const [taskComments, setTaskComments] = useState<string>('');
  const [comingfromSignAndStamp, setComingfromSignAndStamp] = useState<boolean>(false);
  const [needsAttention, setNeedsAtention] = useState<boolean>(false);
  const [isFISignAndStamp, setIsFISignAndStamp] = useState<boolean>(false);
  const [previousTaskComments, setPreviousTaskComments] = useState<PreviousTaskCommentsDocument[]>(
    [],
  );
  const [isLiSignAndStampEnabled, setIsLiSignAndStampEnabled] = useState<
    HTMLButtonElement | null | boolean
  >(null);
  const [previousTaskCommentsSubscription, setPreviousTaskCommentsSubscription] =
    useState<Subscription>();
  const [confirmationForCompleteInspection, setConfirmationForCompleteInspection] =
    useState<boolean>(false);
  const [confirmationForCompletingPMChecklist, setConfirmationForCompletingPMChecklist] =
    useState<boolean>(false);
  const [completeInspectionPayload, setCompleteInspectionPayload] = useState<TaskList>(
    {} as TaskList,
  );
  const [dateError, setDateError] = useState({
    requiredDate: false,
    isInvalidDate: false,
    surpassingMaxDate: false,
  });
  const [selectedProxyInspector, setSelectedProxyInspector] = useState({
    fieldInspectorName: '',
  });

  // constants
  const isElectrical = SiteDiscipline.disciplineId === Discipline.Electrical;
  const savedDate = CompletionDate.getCompletedDate;
  const currentInspectorId = CurrentUserSelected?.currentUser?.inspectorId;
  const taskId = String(CurrentUserSelected.taskId);
  const isAdhoc = CurrentUserSelected.isAdhoc;
  const isLiFiSameAndAdhoc = isLiFiSame && isAdhoc;

  // functions

  const getPreviousTaskCommentsFromRxdb = async () => {
    const previousTaskCommentsDocument = await getPreviousTaskComments(Number(taskId));
    if (previousTaskCommentsDocument?.length) {
      setPreviousTaskComments(JSON.parse(JSON.stringify(previousTaskCommentsDocument)));
    }
  };

  const subscribeToPreviousTaskComments = async () => {
    if (previousTaskCommentsInitialized && previousTaskCommentsCollection) {
      const pcSub = previousTaskCommentsCollection.$.subscribe(() => {
        getPreviousTaskCommentsFromRxdb();
      });
      setPreviousTaskCommentsSubscription(pcSub);
    }
  };

  const goToPMChecklist = () => {
    history.push('/PMChecklist');
  };

  const handleNeedAttentionChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setNeedsAtention(event.target.checked);
    setShowComment(true);
  };

  const onRecommendationsInput = (event: React.FocusEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const tempData = {
      ...completeInspectionPayload,
      recommendations: value,
    };
    setCompleteInspectionPayload(tempData);
    saveInspection.setDiscardData(tempData);
    if (!(dateError.isInvalidDate || dateError.surpassingMaxDate)) {
      saveInspection.setIsChanged(tempData.recommendations !== taskData?.recommendations);
      saveInspection.setIsSaved(!(tempData.recommendations !== taskData?.recommendations));
    }
    if (savedDate.date === '' || dateError.requiredDate) {
      saveInspection.setIsSaved(true);
    }
  };

  const submitInspection = () => {
    if (
      taskData?.fkInspectorField !== taskData?.fkInspectorLead &&
      taskData?.fkInspectionTaskStatusCategory === TaskStatus.FI_SIGN_PENDING &&
      !isAdhoc
    ) {
      setIsFISignAndStamp(true);
      setIsLiSignAndStampEnabled(true);
    } else if (
      isLiFiSame &&
      taskData?.fkInspectionTaskStatusCategory === TaskStatus.LI_SIGN_PENDING &&
      selectedInspection?.assetInspectionStatusId === InspectionStatus.LI_SIGN_PENDING &&
      !isAdhoc
    ) {
      setIsLiSignAndStampEnabled(true);
    } else {
      const childrenCount = pmCheckListLookUpData?.map((item: PmCheckList) => {
        return item.children?.length > 0 ? item?.children?.length : 1;
      });
      let savedpmchecklist = [];
      let result = 0;
      if (childrenCount?.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        result = childrenCount?.reduce((acc: any, curr: any) => acc + curr);
        savedpmchecklist = pmChecklistTransactionData?.filter(
          pmChecklist => pmChecklist.isCompleted !== null,
        );
      }
      if (isElectrical && savedpmchecklist?.length !== result) {
        setConfirmationForCompletingPMChecklist(true);
      } else {
        setConfirmationForCompleteInspection(true);
      }
    }
  };

  const getStatusAndMessageOnReview = () => {
    const assignedByDEC = !!taskData?.clerkId;
    return getStatusAndMessageOnReviewFromUtility(
      isSubmitToSC,
      isLiFiSame,
      isAdhoc,
      isFISignAvailable,
      isLISignAvailable,
      assignedByDEC,
    );
  };

  const getStatusAndMessageOnSubmit = () => {
    const isFiScSame = taskData?.siteCoordinatorUserId === taskData?.fieldInspectorUserId;
    return getStatusAndMessageOnSubmitFromUtility(
      isFiScSame,
      isLiFiSame,
      isAdhoc,
      isFISignAvailable,
      isLISignAvailable,
      isDECUser,
    );
  };

  const handleRequired = () => {
    setDateError({
      requiredDate: true,
      isInvalidDate: false,
      surpassingMaxDate: false,
    });
    saveInspection.setIsSaved(true);
    saveInspection.setIsChanged(true);
    CompletionDate.updateCompletionDate({
      taskId,
      date: '',
    });
  };

  const handleChange = (e: MaterialUiPickersDate) => {
    if (e != 'Invalid Date') {
      setDateError({
        requiredDate: false,
        isInvalidDate: false,
        surpassingMaxDate: false,
      });
      if (dayjs(e).isAfter(new Date())) {
        setDateError({
          requiredDate: false,
          isInvalidDate: false,
          surpassingMaxDate: true,
        });
        saveInspection.setIsChanged(true);
        saveInspection.setIsSaved(true);
        CompletionDate.updateCompletionDate({
          taskId,
          date: e?.toISOString() ? e?.toISOString() : '',
        });
        const tempData = {
          ...completeInspectionPayload,
          dateCompleted: e?.toISOString(),
        };
        saveInspection.setDiscardData(tempData);
      } else {
        CompletionDate.updateCompletionDate({
          taskId,
          date: e?.toISOString() || '',
        });
        const tempData = {
          ...completeInspectionPayload,
          dateCompleted: e?.toISOString(),
        };
        saveInspection.setDiscardData(tempData);
        saveInspection.setIsChanged(true);
        saveInspection.setIsSaved(false);
      }
    } else {
      saveInspection.setIsChanged(true);
      saveInspection.setIsSaved(true);
      setDateError({
        requiredDate: false,
        isInvalidDate: true,
        surpassingMaxDate: false,
      });
      CompletionDate.updateCompletionDate({
        taskId,
        date: '',
      });
    }
  };

  const handleProxyInspectorChange = (inspector: { fieldInspectorName: string }) => {
    const { fieldInspectorName } = inspector;
    const tempData = {
      ...completeInspectionPayload,
      proxyInspector: fieldInspectorName,
    };
    setSelectedProxyInspector({ fieldInspectorName });
    setCompleteInspectionPayload(tempData);
    saveInspection.setDiscardData(tempData);
    saveInspection.setIsChanged(true);
    saveInspection.setIsSaved(false);
  };

  const handleSave = async () => {
    const { tempData } = saveInspection;
    const finalPayload = {
      dateCompleted: isNull(savedDate, 'date', new Date().toISOString()),
      recommendations: isNull(tempData, 'recommendations', ''),
      proxyInspector: isNull(tempData, 'proxyInspector', ''),
    };
    saveInspection.setIsSaved(true);
    saveInspection.setIsChanged(false);
    try {
      await setTaskRecommendation(finalPayload);
      successNotification('Saved Successfully!');
    } catch (error) {
      errorNotification('Error while saving data please try again');
      sentryCaptureException(error, '~ file: CompleteInspection.tsx ~ line 275 ~ handleSave ~');
    }
  };

  const checkIfAllTasksAreLISign = () => {
    const allTasksUnderInspection = selectedInspection?.taskList;
    const siblingTasks = allTasksUnderInspection?.filter(
      task => task.fkInspectionTaskStatusCategory === TaskStatus.LI_SIGN_PENDING,
    );

    return siblingTasks?.length === allTasksUnderInspection?.length - 1;
  };

  const completeInspection = async () => {
    setIsCompleting(true);
    try {
      const submitParameters = getStatusAndMessageOnSubmit();
      const result = await setTaskListInfo(
        submitParameters.status,
        true,
        isNull(savedDate, 'date', new Date().toISOString()),
        isNull(selectedProxyInspector, 'fieldInspectorName', ''),
      );
      if (
        (!isAdhoc &&
          checkIfAllTasksAreLISign() &&
          currentInspectorId === taskData?.fkInspectorLead &&
          result &&
          !isLISignAvailable) ||
        (submitParameters?.isLiSignEnabled && result)
      ) {
        // condition for redirect to SignAndStamp page for LISign
        setConfirmationForCompleteInspection(false);
        setIsFISignAndStamp(false);
        setIsLiSignAndStampEnabled(true);
      } else if (submitParameters.status === TaskStatus.FI_SIGN_PENDING) {
        // condition for redirect to SignAndStamp page for  FISign
        setIsFISignAndStamp(true);
        setConfirmationForCompleteInspection(false);
        setIsLiSignAndStampEnabled(true);
      } else if (result) {
        // No sign required then submit inspection and show inpection list
        history.push(`/${isAdhoc ? 'work-packages' : 'inspection-list'}`);
        successNotification('Inspection Submitted Successfully!');
      }
    } catch (error) {
      errorNotification('Error while submitting task please try again');
      sentryCaptureException(
        error,
        '~ file: CompleteInspection.tsx ~ line 310 ~ completeInspection ~',
      );
    } finally {
      saveInspection.setIsSaved(true);
      saveInspection.setIsChanged(false);
      setIsCompleting(false);
    }
  };

  const openConfirmation = (value: boolean) => {
    if (
      taskData?.fkInspectionTaskStatusCategory === TaskStatus.LI_SIGN_PENDING &&
      !isAdhoc &&
      value
    ) {
      setIsFISignAndStamp(false);
      setIsLiSignAndStampEnabled(true);
    } else {
      setIsSubmitToSC(value);
      setOpenConfirmationForAssignOrSubmit(true);
    }
  };

  const isLiSignEnabledForNonAdhocInspection = () => {
    const siblingTasks = selectedInspection?.taskList?.filter(
      task =>
        task.pkAssetInspectionTask !== CurrentUserSelected.taskId &&
        ![
          TaskStatus.LI_SIGN_PENDING,
          TaskStatus.SC_PENDING_APPROVAL,
          TaskStatus.COMPLETED,
        ].includes(task.fkInspectionTaskStatusCategory),
    );

    if (siblingTasks.length === 0) return true;
    return false;
  };

  const assignBackOrSubmit = async () => {
    if (taskData) {
      setIsCompleting(true);
      try {
        const reviewParameters = getStatusAndMessageOnReview();
        if (!isAdhoc && isSubmitToSC) {
          reviewParameters.isLiSignEnabled =
            !isLISignAvailable && isLiSignEnabledForNonAdhocInspection();
        }
        const result = await setTaskLevelComment(
          taskData?.pkAssetInspectionTask,
          taskData?.commentInspectorLead,
          taskData?.commentInspectorField,
          reviewParameters.status,
          !isSubmitToSC,
          taskData?.attentionFlag,
        );
        if (reviewParameters?.isLiSignEnabled && result) {
          setOpenConfirmationForAssignOrSubmit(false);
          setIsLiSignAndStampEnabled(true);
        } else if (result) {
          history.push(`/${isAdhoc ? 'work-packages' : 'inspection-list'}`);
          successNotification(reviewParameters.notification);
        }
      } catch (error) {
        errorNotification('Not Saved Successfully Please Try Again.');
        sentryCaptureException(
          error,
          '~ file: CompleteInspection.tsx ~ line 356 ~ assignBackOrSubmit ~',
        );
      }
      setIsCompleting(false);
      setOpenConfirmationForAssignOrSubmit(false);
    }
  };

  const validateFIStamping = () => {
    if (!isAdhoc) {
      return (
        [taskData?.fkInspectorLead, taskData?.fkInspectorField].includes(currentInspectorId) &&
        ([
          TaskStatus.LI_PENDING_APPROVAL,
          TaskStatus.SC_PENDING_APPROVAL,
          TaskStatus.COMPLETED,
        ].includes(taskData?.fkInspectionTaskStatusCategory || 0) ||
          (taskData?.fkInspectionTaskStatusCategory === TaskStatus.LI_SIGN_PENDING &&
            taskData.fkInspectorField !== taskData.fkInspectorLead))
      );
    }
    return (
      (taskData?.fkInspectorLead === currentInspectorId &&
        taskData?.fkInspectionTaskStatusCategory === TaskStatus.LI_SIGN_PENDING) ||
      (taskData?.fkInspectorField === currentInspectorId &&
        (taskData?.fkInspectionTaskStatusCategory === TaskStatus.FI_SIGN_PENDING ||
          taskData?.fkInspectionTaskStatusCategory === TaskStatus.LI_SIGN_PENDING)) ||
      ((taskData?.fkInspectorLead === currentInspectorId ||
        taskData?.fkInspectorField === currentInspectorId) &&
        taskData?.fkInspectionTaskStatusCategory === TaskStatus.COMPLETED)
    );
  };

  const onInspectorCommentChange = (event: React.FocusEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setTaskComments(value);
    setShowComment(true);
  };

  const validateLeadComment = () => {
    return taskData?.fkInspectorLead === currentInspectorId;
  };

  const checkIFBothLiFi = () => {
    return (
      taskData?.fkInspectorLead === currentInspectorId &&
      taskData?.fkInspectorLead === taskData?.fkInspectorField
    );
  };

  const handleSaveComment = async () => {
    if (taskData) {
      try {
        if (taskComments !== '') {
          const result = await setTaskComment(
            taskData?.pkAssetInspectionTask,
            taskComments,
            needsAttention,
          );
          if (result) {
            successNotification('Saved Successfully!');
          }
        }
        setShowComment(false);
      } catch (error) {
        errorNotification('Not Saved Successfully Please Try Again.');
        sentryCaptureException(
          error,
          '~ file: CompleteInspection.tsx ~ line 424 ~ handleSaveComment ~',
        );
      }
    }
  };

  const handleCancelComment = async () => {
    setShowComment(false);
    setTaskComments(isNull(taskData, 'commentInspectorField', ''));
    setNeedsAtention(taskData?.attentionFlag || false);
  };

  const RenderAccordian = (label: JSX.Element, value: JSX.Element) => {
    return (
      <Accordion
        defaultExpanded={!isAdhoc || !validateFIStamping()}
        className={classes.accordianContainer}
      >
        <AccordionSummary
          expandIcon={<Icons.ChevronDownFeather color="primary" />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          {label}
        </AccordionSummary>
        <AccordionDetails>{value}</AccordionDetails>
      </Accordion>
    );
  };

  const checkValid = () => {
    return checkIFBothLiFi()
      ? false
      : validateLeadComment()
      ? (taskComments === isNull(taskData, 'commentInspectorField', '') &&
          needsAttention === taskData?.attentionFlag) ||
        (needsAttention && !taskComments?.length)
      : taskComments === isNull(taskData, 'commentInspectorField', '');
  };

  const handleKeyDown = async (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (savedDate.date === '' || savedDate.date === null) {
      setDateError({
        requiredDate: false,
        isInvalidDate: true,
        surpassingMaxDate: false,
      });
      saveInspection.setIsSaved(true);
      saveInspection.setIsChanged(true);
      CompletionDate.updateCompletionDate({
        taskId,
        date: '',
      });
      return false;
    }
    if (event.key === Key.Enter && (saveInspection.isSaved || !saveInspection.isChanged)) {
      submitInspection();
    } else if (event.key === Key.Enter && !saveInspection.isSaved) {
      await handleSave();
      saveInspection.setIsSaved(true);
      saveInspection.setIsChanged(false);
    }
  };

  const completeInspectionAccordion = () => {
    return (
      <CompleteInspectionAccordion
        validateFIStamping={validateFIStamping}
        taskData={taskData}
        isElectrical={isElectrical}
        completeInspectionPayload={completeInspectionPayload}
        openConfirmation={openConfirmation}
        needsAttention={needsAttention}
        isAttentionFlag={isAttentionFlag}
        taskComments={taskComments}
        submitInspection={submitInspection}
        onRecommendationsInput={onRecommendationsInput}
        handleKeyDown={handleKeyDown}
        handleChange={handleChange}
        handleRequired={handleRequired}
        handleSave={handleSave}
        onInspectorCommentChange={onInspectorCommentChange}
        showComment={showComment}
        handleCancelComment={handleCancelComment}
        checkValid={checkValid}
        handleSaveComment={handleSaveComment}
        previousTaskComments={previousTaskComments}
        handleNeedAttentionChange={handleNeedAttentionChange}
        isLiFiSameAndAdhoc={isLiFiSameAndAdhoc}
        invalidDate={dateError.isInvalidDate}
        maxDateError={dateError.surpassingMaxDate}
        requiredDate={dateError.requiredDate}
        isLiFiSame={isLiFiSame}
        comingfromSignAndStamp={comingfromSignAndStamp}
        selectedInspection={selectedInspection as unknown as InspectionTaskListType}
        isEditable={isEditable}
        selectedProxyInspector={selectedProxyInspector}
        handleProxyInspectorChange={handleProxyInspectorChange}
      />
    );
  };

  // useEffects
  useEffect(() => {
    if ((deficiencies || activeDeficiencies) && taskData) {
      const allDeficiencies = [...deficiencies, ...activeDeficiencies];
      const attentionedDeficiencies = allDeficiencies
        ?.flat()
        .filter(deficiency => deficiency?.attentionFlag).length;
      if (attentionedDeficiencies > 0 || taskData?.attentionFlag) {
        setIsAttentionFlag(true);
      } else if (attentionedDeficiencies === 0 && taskData?.attentionFlag === false) {
        setIsAttentionFlag(false);
      }
    }
  }, [deficiencies, taskData, activeDeficiencies]);

  useEffect(() => {
    if (taskData) {
      setTaskComments(isNull(taskData, 'commentInspectorField', ''));
      setNeedsAtention(taskData?.attentionFlag);
    }
  }, [taskData]);

  useEffect(() => {
    if (completeInspectionPayload.dateCompleted) {
      CompletionDate.updateCompletionDate({
        taskId,
        date: savedDate.date !== '' ? savedDate.date : completeInspectionPayload.dateCompleted,
      });
    } else {
      CompletionDate.updateCompletionDate({
        taskId,
        date: isNull(savedDate, 'date', ''),
      });
    }
    if (savedDate.date === '') {
      saveInspection.setIsChanged(true);
    }
  }, [completeInspectionPayload, CompletionDate]);

  useEffect(() => {
    if (taskData) {
      setCompleteInspectionPayload(JSON.parse(JSON.stringify(taskData)));
    }
  }, [taskData]);

  useEffect(() => {
    if (selectedInspection?.taskList && selectedInspection?.taskList.length) {
      setSelectedProxyInspector({
        fieldInspectorName: selectedInspection?.taskList[0]?.proxyInspector || '',
      });
    }
  }, [selectedInspection.taskList]);

  useEffect(() => {
    if (previousTaskCommentsInitialized) {
      getPreviousTaskCommentsFromRxdb();
      subscribeToPreviousTaskComments();
    }
    return () => {
      if (previousTaskCommentsSubscription) previousTaskCommentsSubscription.unsubscribe();
    };
  }, [previousTaskCommentsInitialized]);

  return (
    <>
      <Grid
        className={
          configCatFlag.getBlueBannerDisplay === true || configCatFlag.getRedBannerDisplay === true
            ? classes.root1
            : classes.root
        }
      >
        <Grid>
          {!validateFIStamping() || isLiFiSameAndAdhoc
            ? completeInspectionAccordion()
            : RenderAccordian(
                <Grid className={classes.accordianGrid}>
                  <Typography className="accordianTitle">
                    {isElectrical ? 'Complete Preventative Maintenance' : 'Complete Inspection'}
                  </Typography>
                </Grid>,
                completeInspectionAccordion(),
              )}
        </Grid>
        {validateFIStamping() && (!isAdhoc || !isLiFiSameAndAdhoc) && taskData && (
          <Grid className={classes.accordianContainer}>
            <SignAndStampFI taskData={taskData} />
          </Grid>
        )}
      </Grid>

      {confirmationForCompleteInspection && (
        <Dialog
          onClose={() => setConfirmationForCompleteInspection(false)}
          open={confirmationForCompleteInspection}
          titleText={getStatusAndMessageOnSubmit().title}
          backButton={false}
          dialogActions={
            <Grid container justifyContent="space-between" spacing={2}>
              <Grid item className={classes.buttonTextGrid}>
                <Button
                  className="buttonText"
                  variant="outlined"
                  color="primary"
                  noMinHeight
                  onClick={() => setConfirmationForCompleteInspection(false)}
                  data-testid="cancelbutton"
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item className={classes.buttonTextGrid}>
                <Button
                  variant="contained"
                  color="primary"
                  className="buttonText"
                  noMinHeight
                  isLoading={isCompleting}
                  onClick={completeInspection}
                  data-testid="yesbutton"
                >
                  Yes, Submit
                </Button>
              </Grid>
            </Grid>
          }
          maxWidth="xs"
          noMinHeight
        >
          <Grid container className={classes.confirmationForCIGrid}>
            <Grid item container xs={12} alignItems="center">
              <Typography className="confirmationForCIContent">
                {getStatusAndMessageOnSubmit().message}
              </Typography>
            </Grid>
          </Grid>
        </Dialog>
      )}

      {confirmationForCompletingPMChecklist && (
        <Dialog
          onClose={() => setConfirmationForCompletingPMChecklist(false)}
          open={confirmationForCompletingPMChecklist}
          titleText="Confirmation"
          backButton={false}
          dialogActions={
            <Grid container justifyContent="space-between" spacing={2}>
              <Grid item className={classes.dialogActionButtonGrid}>
                <Button
                  variant="contained"
                  color="primary"
                  className="buttonText"
                  noMinHeight
                  onClick={goToPMChecklist}
                  data-testid="pmChecklistButton"
                >
                  Go to PM Checklist
                </Button>
              </Grid>
            </Grid>
          }
          maxWidth="xs"
          noMinHeight
        >
          <Grid container className={classes.confirmationForPMCheclist}>
            <Grid item container xs={12} alignItems="center">
              <Typography className="confirmationForPMChecklistContent">
                Please complete the PM Checklist before submitting the Preventative Maintenance
              </Typography>
            </Grid>
          </Grid>
        </Dialog>
      )}

      {openConfirmationForAssignOrSubmit && (
        <ConfirmationOnAssign
          open={openConfirmationForAssignOrSubmit}
          onClose={() => setOpenConfirmationForAssignOrSubmit(false)}
          isLoading={isCompleting}
          onSave={assignBackOrSubmit}
          header={getStatusAndMessageOnReview().title}
          confirmMessage={isSubmitToSC ? 'Submit' : 'Assign'}
          message={getStatusAndMessageOnReview().message}
        />
      )}
      {isLiSignAndStampEnabled && (
        <SignAndStamp
          openSignAndStamp={Boolean(isLiSignAndStampEnabled)}
          anchorEl={isLiSignAndStampEnabled}
          setopenSignAndStamp={() => {
            setIsLiSignAndStampEnabled(null);
            history.push(`/${!isAdhoc || isFISignAndStamp ? 'inspection-list' : 'work-packages'}`);
          }}
          inspectionData={selectedInspection as unknown as InspectionTaskListType}
          isFISignAndStamp={isFISignAndStamp}
          isLiFiSame={isLiFiSame}
          isSignAndStampFromInspectionList={false}
          setComingfromSignAndStamp={setComingfromSignAndStamp}
        />
      )}
    </>
  );
};

export default observer(CompleteInspection);
